import { Target, CheckCircle, Eye, Factory } from 'lucide-react';
import { companyInfo, brands } from '../../data/mock';
import { useInView } from '../../hooks/useInView';

export const AboutSection = () => {
  const [ref, isInView] = useInView();

  return (
    <section id="about" className="py-20 lg:py-28 bg-[hsl(210,20%,98%)]" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left - Content */}
          <div className={`${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="section-divider" />
              <span className="text-sm font-semibold text-ind-red-600 uppercase tracking-wider">About Us</span>
            </div>
            <h2 className="font-heading font-extrabold text-navy-950 text-3xl lg:text-4xl mb-6">
              About V.R. Enterprises
            </h2>
            <p className="text-navy-600 text-base leading-relaxed mb-6">
              {companyInfo.about}
            </p>

            {/* Highlights */}
            <div className="space-y-3 mb-8">
              {companyInfo.highlights.map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-ind-red-50 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-3.5 h-3.5 text-ind-red-600" />
                  </div>
                  <span className="text-sm font-medium text-navy-700">{item}</span>
                </div>
              ))}
            </div>

            {/* Vision */}
            <div className="bg-white rounded-xl border border-navy-100 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-ind-red-600" />
                <h3 className="font-heading font-bold text-navy-900 text-lg">Our Vision</h3>
              </div>
              <p className="text-navy-600 text-sm leading-relaxed italic">
                "{companyInfo.vision}"
              </p>
            </div>
          </div>

          {/* Right - Brands overview + Factory */}
          <div className={`${isInView ? 'animate-fade-in-up' : 'opacity-0'} space-y-6`} style={{ animationDelay: '0.2s' }}>
            <div className="bg-navy-950 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute inset-0 industrial-pattern" />
              <div className="relative">
                <div className="flex items-center gap-2 mb-4">
                  <Factory className="w-5 h-5 text-ind-red-400" />
                  <h3 className="font-heading font-bold text-white text-lg">Our Brands</h3>
                </div>
                <div className="space-y-3">
                  {brands.map((brand) => (
                    <div key={brand.name} className="flex items-center gap-4 bg-navy-900/60 rounded-lg p-3 border border-navy-800">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center font-heading font-extrabold text-white text-xs flex-shrink-0"
                        style={{ backgroundColor: brand.color }}
                      >
                        {brand.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-white text-sm">{brand.name}</p>
                        <p className="text-xs text-navy-400">{brand.tagline}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Stats bar */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white rounded-xl p-5 border border-navy-100 text-center shadow-sm">
                <p className="font-heading font-extrabold text-2xl text-navy-950">1984</p>
                <p className="text-xs text-navy-500 mt-1">Established</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-navy-100 text-center shadow-sm">
                <p className="font-heading font-extrabold text-2xl text-navy-950">5</p>
                <p className="text-xs text-navy-500 mt-1">Brands</p>
              </div>
              <div className="bg-white rounded-xl p-5 border border-navy-100 text-center shadow-sm">
                <p className="font-heading font-extrabold text-2xl text-navy-950">12+</p>
                <p className="text-xs text-navy-500 mt-1">Product Lines</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
